/**
 * Los formatos de TABLA: todos contra todos, en uno o en dos grupos.
 *
 * POR QUÉ NO SE DIBUJA COMO UN CUADRO. En las llaves lo que importa es contra
 * quién se juega después; en una tabla lo que importa es EN QUÉ LUGAR VA
 * CADA QUIEN, y eso no se ve en un árbol.
 *
 * CADA GRUPO ES UNA TARJETA, y esa es la corrección de fondo. Antes los dos
 * grupos salían uno pegado al otro, con siete columnas de números cada uno y
 * las quince jornadas de partidos intercaladas en el medio: en un teléfono
 * eso no es una tabla, es un muro que además se arrastra de lado. Ahora cada
 * grupo es una tarjeta cerrada, con su nombre, cuánto lleva jugado y el corte
 * de clasificación dibujado donde de verdad cae.
 *
 * DOS COLUMNAS DE NÚMEROS Y NO SIETE. Ganados y perdidos, que es a lo que se
 * juega. Sets y games siguen existiendo —son los que desempatan— pero viven
 * detrás de «Ver detalles»: son la EXPLICACIÓN de un orden, y una explicación
 * se lee cuando se pregunta, no todo el tiempo. Con siete columnas fijas, la
 * tabla no cabía en ningún teléfono y el precio lo pagaba el dato principal.
 *
 * Y LOS PARTIDOS YA NO VAN ACÁ. Antes cada grupo arrastraba su lista por
 * jornada, así que «¿qué se juega el sábado?» se contestaba recorriendo dos
 * listas y sumando de cabeza. Ahora hay UNA agenda para todo el torneo,
 * debajo de todo, ordenada por cuándo se juega: `NextMatches`. Ver
 * `TournamentPage`.
 *
 * LA TABLA SE CALCULA EN EL CONTRATO, no acá. `buildStandings` es la misma
 * función que usa el servidor para decidir quién pasa a la semifinal: si la
 * pantalla ordenara con su propia idea del desempate, mostraría de primero a
 * alguien que el servidor no clasificó, y no habría forma de explicarlo.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  advancingPerGroup,
  buildStandings,
  groupCountOf,
  groupOfMatch,
  snakeIntoGroups,
} from '@tennisup/contracts';
import type {
  GroupStanding,
  Match,
  RoundDeadline,
  TournamentEntry,
  TournamentFormat,
} from '@tennisup/contracts';

import { Avatar } from './Avatar';
import { Bracket } from './Bracket';
import { assetUrl } from '../lib/api';

interface GroupsProps {
  format: TournamentFormat;
  /** Para el mensaje de WhatsApp: «te escribo por el Abierto de Grecia». */
  tournamentName: string;
  /** Los partidos de UNA categoría: los de tabla y los de definición. */
  matches: readonly Match[];
  /** Las inscripciones de esa misma categoría. */
  entries: readonly TournamentEntry[];
  onScore?: (match: Match) => void;
  onSchedule?: (match: Match) => void;
  /** Las fechas límite de ESTA categoría. Solo valen para la definición: en
      una tabla las jornadas se programan partido por partido. */
  deadlines?: readonly RoundDeadline[];
  onDeadline?: (players: number, date: string | null) => void;
}

export function Groups({
  format,
  tournamentName,
  matches,
  entries,
  onScore,
  onSchedule,
  deadlines,
  onDeadline,
}: GroupsProps): JSX.Element {
  const { t } = useTranslation();

  /*
   * QUÉ ES DE TABLA Y QUÉ ES DE DEFINICIÓN LO DECIDE `groupOfMatch`, y no
   * `m.group !== null` como antes. La diferencia se ve en los torneos viejos:
   * `group` se agregó después, así que esas filas lo tienen en `null` aunque
   * sean partidos de grupo. Mirando solo ese campo, las quince jornadas de un
   * todos-contra-todos caían acá abajo y se dibujaban COMO UN CUADRO —cinco
   * rondas, «Ronda de 32», treinta lugares vacíos escritos «Bye»— mientras la
   * tabla de arriba quedaba en blanco. Ver `groupOfMatch` en el contrato.
   */
  const deGrupo = matches.filter((m) => groupOfMatch(m) !== null);
  const definición = matches.filter((m) => groupOfMatch(m) === null);
  // EL FORMATO VA SÍ O SÍ: es lo que elige el desempate. Sin él, `buildStandings`
  // usa el del club —mano a mano antes que los sets— y las Finals se ordenan con
  // el de la ATP. La pantalla mostraba un orden y el servidor sembraba la
  // semifinal con otro, que es justo lo que el encabezado de este archivo dice
  // que no puede pasar.
  const tablas = buildStandings(entries, matches, format);
  const pasan = advancingPerGroup(format);
  const hayDefinición = pasan > 0;
  const vacía = definición.every((m) => m.home === null && m.away === null);
  // Una sola tabla no lleva nombre de grupo: decir «Grupo A» cuando no hay B
  // es hacer buscar un grupo que no existe.
  const conNombre = tablas.length > 1;

  return (
    <div className="groups">
      {tablas.map((tabla) => (
        <GroupCard
          key={tabla.group}
          tabla={tabla}
          matches={deGrupo.filter((m) => groupOfMatch(m) === tabla.group)}
          pasan={pasan}
          conNombre={conNombre}
        />
      ))}

      {hayDefinición && definición.length > 0 && (
        <section className="group-card">
          <div className="group-head">
            <h4>{t('groups.playoff')}</h4>
          </div>
          {/*
            UN LUGAR VACÍO ACÁ NO ES UN «BYE», Y DIBUJARLO ASÍ ES MENTIR.

            En un cuadro, un hueco significa «este pasa sin jugar»; en una
            definición que todavía no se sembró significa «no se sabe quién
            viene», que es lo contrario —nadie pasó nada—. Con la final vacía
            se veía «Bye contra Bye» debajo del cartel que dice que se llena
            cuando terminen los grupos: dos frases que se contradicen.

            Así que mientras no haya UNA sola persona colocada, se dice la
            frase y no se dibuja nada. El cuadro aparece cuando hay algo que
            mostrar en él.
          */}
          {vacía ? (
            <p className="help">{t('groups.playoffPending')}</p>
          ) : (
          <Bracket
            matches={definición}
            tournamentName={tournamentName}
            {...(onScore ? { onScore } : {})}
            {...(onSchedule ? { onSchedule } : {})}
            {...(deadlines ? { deadlines } : {})}
            {...(onDeadline ? { onDeadline } : {})}
          />
          )}
        </section>
      )}
    </div>
  );
}

/**
 * UNA TABLA, COMPLETA: quién va ganando, cuánto falta y quién clasifica.
 *
 * EL CORTE SE DIBUJA DONDE CAE. «Clasificados» va como una línea entre el
 * último que pasa y el primero que no, porque esa línea ES la información:
 * saber que uno va tercero no dice nada si no se sabe cuántos pasan. Cuando
 * el formato no tiene definición —un todos contra todos que termina y ya— no
 * hay corte que dibujar y no se dibuja ninguno.
 *
 * EL CONTADOR DE PARTIDOS ES LO QUE HACE HONESTA A LA TABLA. Con dos de seis
 * partidos jugados, el primer lugar no significa casi nada, y quien la mira
 * tiene derecho a saberlo antes de sacar conclusiones.
 */
function GroupCard({
  tabla,
  matches,
  pasan,
  conNombre,
}: {
  tabla: GroupStanding;
  matches: readonly Match[];
  pasan: number;
  conNombre: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [verDetalles, setVerDetalles] = useState(false);

  const jugados = matches.filter((m) => m.winnerEntryId !== null).length;
  const letra = letraDe(tabla.group);

  return (
    <section className="group-card">
      <div className="group-head">
        <h4>{conNombre ? t('groups.name', { letter: letra }) : t('groups.table')}</h4>
        {/*
          `aria-expanded` y no un simple botón: lo que hace no es navegar, es
          abrir y cerrar algo que está acá mismo, y quien usa lector de
          pantalla necesita saber en cuál de los dos estados está.
        */}
        <button
          type="button"
          className="link group-details-toggle"
          aria-expanded={verDetalles}
          onClick={() => setVerDetalles((v) => !v)}
        >
          {verDetalles ? t('groups.hideDetails') : t('groups.seeDetails')}
        </button>
      </div>

      <div className="group-sub">
        <span className="help">{t('groups.playedCount', { done: jugados, total: matches.length })}</span>
        <span className="help">{t('groups.recordHeader')}</span>
      </div>

      <ol className="standings-list">
        {tabla.rows.map((row, i) => {
          // El corte va ARRIBA del primero que clasifica, no debajo del
          // último: así se lee «de acá para arriba pasan», que es el orden en
          // que la vista se recorre.
          const primeroQuePasa = pasan > 0 && i === 0;
          const clasifica = pasan > 0 && i < pasan;
          const primeroQueNoPasa = pasan > 0 && i === pasan;

          return (
            <li key={row.entryId}>
              {primeroQuePasa && (
                <p className="standings-cut">{t('groups.qualifying')}</p>
              )}
              {primeroQueNoPasa && <p className="standings-line" aria-hidden="true" />}

              <div className={clasifica ? 'standings-row is-qualifying' : 'standings-row'}>
                <span className="standings-seed" title={t('groups.seedLegend')}>
                  {row.seed}
                </span>
                <Avatar url={assetUrl(row.avatarPath)} name={row.displayName} size={34} />
                <span className="standings-who">
                  <span className="bracket-nick">{row.displayName}</span>
                  {clasifica ? (
                    <span className="standings-berth">
                      {t('groups.advancesAs', { slot: `${letra}${row.position}` })}
                    </span>
                  ) : (
                    row.firstName !== '' && <span className="bracket-first">{row.firstName}</span>
                  )}
                </span>
                <span className="standings-record">
                  <span className="standings-won">
                    {row.won}
                    <span aria-hidden="true"> ✅</span>
                    <span className="visually-hidden"> {t('groups.wonLong')}</span>
                  </span>
                  <span className="standings-lost">
                    {row.lost}
                    <span aria-hidden="true"> ❌</span>
                    <span className="visually-hidden"> {t('groups.lostLong')}</span>
                  </span>
                </span>
              </div>
            </li>
          );
        })}
      </ol>

      <p className="help standings-legend">{t('groups.sortedBy')}</p>

      {verDetalles && <DetailsTable tabla={tabla} />}
    </section>
  );
}

/**
 * EL DESGLOSE: por qué está en ese lugar y no en otro.
 *
 * Son las mismas columnas que decide el desempate y EN ESE ORDEN —partidos,
 * después sets, después games—, así que quien no entiende el orden tiene las
 * tres razones a la vista, una al lado de la otra.
 *
 * Se desplaza sola a lo ancho: son siete columnas y en un teléfono angosto no
 * caben. Acá sí está bien que se arrastre, porque el que la abrió la abrió a
 * propósito; lo que estaba mal era que se arrastrara la tabla principal.
 */
function DetailsTable({ tabla }: { tabla: GroupStanding }): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="table-scroll group-details">
      <table className="standings">
        <thead>
          <tr>
            <th scope="col" className="standings-pos">
              #
            </th>
            <th scope="col">{t('groups.player')}</th>
            <th scope="col" title={t('groups.playedLong')}>
              {t('groups.played')}
            </th>
            <th scope="col" title={t('groups.wonLong')}>
              {t('groups.won')}
            </th>
            <th scope="col" title={t('groups.lostLong')}>
              {t('groups.lost')}
            </th>
            <th scope="col" title={t('groups.setsLong')}>
              {t('groups.sets')}
            </th>
            <th scope="col" title={t('groups.gamesLong')}>
              {t('groups.games')}
            </th>
          </tr>
        </thead>
        <tbody>
          {tabla.rows.map((row) => (
            <tr key={row.entryId}>
              <td className="standings-pos">{row.position}</td>
              <td>
                <span className="standings-player">
                  <Avatar url={assetUrl(row.avatarPath)} name={row.displayName} size={22} />
                  <span className="bracket-name">
                    <span className="bracket-nick">{row.displayName}</span>
                    {row.firstName !== '' && <span className="bracket-first">{row.firstName}</span>}
                  </span>
                </span>
              </td>
              <td>{row.played}</td>
              <td>{row.won}</td>
              <td>{row.lost}</td>
              <td>
                {row.setsWon}–{row.setsLost}
              </td>
              <td>
                {row.gamesWon}–{row.gamesLost}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * QUIÉN VA A CAER EN CADA TABLA, antes de tirar el cuadro.
 *
 * Los grupos no existen todavía —se arman al tirar— pero quien organiza
 * necesita verlos ANTES: es lo único que se puede corregir, acomodando la
 * siembra o volviendo a sortear. Sin esta vista, la única forma de saber si
 * los dos favoritos quedaron en la misma tabla era tirar el cuadro, que es
 * justamente lo que ya no se puede deshacer.
 *
 * EL REPARTO NO SE ADIVINA ACÁ: es `snakeIntoGroups` del contrato, la misma
 * función que va a usar el servidor con la misma lista ordenada por siembra.
 * Lo que se ve es lo que va a pasar, no una aproximación.
 */
export function GroupPreview({
  format,
  entries,
}: {
  format: TournamentFormat;
  entries: readonly TournamentEntry[];
}): JSX.Element {
  const { t } = useTranslation();

  const grupos = groupCountOf(format);
  const porSiembra = [...entries].sort((a, b) => a.seed - b.seed);
  const repartidos = snakeIntoGroups(porSiembra, Math.max(grupos, 1));
  const conNombre = repartidos.length > 1;

  if (entries.length === 0) return <p className="help">{t('tournaments.noEntriesYet')}</p>;

  return (
    <>
      <p className="help">{conNombre ? t('groups.previewHelp') : t('groups.previewOneHelp')}</p>
      <div className="groups groups-preview">
        {repartidos.map((delGrupo, group) => (
          <section className="group-card" key={group}>
            {conNombre && (
              <div className="group-head">
                <h4>{t('groups.name', { letter: letraDe(group) })}</h4>
              </div>
            )}
            <ul className="entry-list">
              {delGrupo.map((entry) => (
                <li key={entry.id}>
                  {/* La siembra SÍ se muestra acá, al revés que en la lista
                      de inscritos: es el número que decide este reparto, y
                      sin él la vista previa no se puede explicar. */}
                  <span className="bracket-seed">{entry.seed}</span>
                  <Avatar url={assetUrl(entry.avatarPath)} name={entry.displayName} size={32} />
                  <span className="bracket-name">
                    <span className="bracket-nick">{entry.displayName}</span>
                    {entry.firstName !== '' && (
                      <span className="bracket-first">{entry.firstName}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}

/** 0 → «A», 1 → «B». El número es del dato; la letra es de la pantalla. */
function letraDe(group: number): string {
  return String.fromCharCode(65 + group);
}
